import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type WalletReveal = {
  /** False while the wallet ranked above this one is still deciding; true, once and for good, after. */
  isRevealed: boolean;
  /**
   * "I have finished deciding" — called once per wallet, whether it finished with a
   * button to draw or with nothing. Only the ranking wallet's report is acted on; the
   * others have nobody waiting behind them.
   *
   * Stable for the life of the row, so a wallet may capture it inside the effect that
   * sets itself up without capturing a stale one.
   */
  reportSettled: () => void;
};

export type PrimaryWalletReveal = WalletReveal & {
  /**
   * "I have mounted and started deciding" — which is also what starts the clock the
   * wallets below this one are waiting against. It has to be reported rather than timed
   * from the row, because the row outlives the question: `CartWalletButtons` mounts as
   * soon as the cart has a count and renders nothing until the full cart arrives, which
   * for a visitor who loads the page with items already in it is not until they open the
   * drawer. Timed from there, the deadline could be long past before Apple Pay had been
   * asked anything at all.
   */
  reportPending: () => void;
};

export type WalletRow = {
  /** Apple Pay's gate. Open from the first render — it waits for nobody. */
  primary: PrimaryWalletReveal;
  /** Every other wallet's gate. Opens once Apple Pay has finished deciding. */
  secondary: WalletReveal;
};

/**
 * How long a secondary wallet waits for Apple Pay before showing itself anyway.
 *
 * Every path Apple Pay's setup can take reports — success, failure, an unsupported
 * device, an empty wallet — so under normal conditions this never fires. What it covers
 * is the one thing reporting cannot: a promise that never resolves at all.
 * `braintree.client.create` and the wallet's own readiness check are both network calls,
 * and a request a content blocker or a captive network swallows is not refused, it is
 * simply never answered — the same distinction that took every wallet button off this
 * page in #1770.
 *
 * Timed from the moment Apple Pay reports that it has started, which it can be now that
 * there is only ever one wallet on the other side of it. While the wallets waited on
 * each other symmetrically this had to be timed from the most recent answer instead, or
 * a slow connection would expire the deadline *between* two wallets that were both going
 * to arrive.
 */
const WALLET_REVEAL_TIMEOUT = 5000;

/** Nobody is waiting on a secondary wallet, so its report has nothing to do. */
const NOTHING_TO_REPORT = () => {};

/**
 * A gate that is already open — the default for a placement that mounts one wallet on
 * its own and so has nothing to wait for.
 */
export const REVEALED: PrimaryWalletReveal = {
  isRevealed: true,
  reportSettled: NOTHING_TO_REPORT,
  reportPending: NOTHING_TO_REPORT,
};

/**
 * Ranks a row of wallet buttons so that they cannot appear in the wrong order.
 *
 * The wallets initialize independently and take visibly different amounts of time to do
 * it — Apple Pay is the slow one, because it alone has to ask the device whether there
 * is a usable card in the wallet, a native call with a network round trip inside it — so
 * a row that let each one show itself the moment it was ready showed them in the order
 * they happened to land. In the buy box that is only untidy. In the sticky mobile bar it
 * is a bug: the bar has room for two children and shows the first two it has, so a
 * Google Pay that arrives first takes the slot beside Add to Cart and is then visibly
 * swapped out for Apple Pay a moment later, which is what QA reported.
 *
 * That swap only ever runs one way. Apple Pay is the ranking wallet — where a device
 * offers it, it is the one we want in the slot — so nothing it could be shown before
 * would be wrong, and it is gated on nothing at all. Everything below it waits for it,
 * which is enough to make the swap impossible: a secondary wallet cannot take the slot
 * early because it cannot be drawn until the question of whether Apple Pay wants it has
 * already been answered.
 *
 * The previous arrangement held the whole row until *every* wallet had reported, which
 * prevented the same swap and cost more than it had to. Apple Pay's latency became the
 * row's latency in both directions: the visitor who was going to be shown Apple Pay
 * waited for Google to finish deciding first, for no reason anyone could see.
 *
 * The ranking between the secondary wallets is left where #1768 put it — the order they
 * are written in, read by `.product-view-floating-cta` — and is not coordinated here.
 * Two of them can only ever be on screen together below Apple Pay's slot, where the bar
 * is not choosing between them.
 *
 * Every row this is used in mounts Apple Pay unconditionally, which is what the secondary
 * gate depends on: it is opened by Apple Pay and by nothing else, so a row that left it
 * out would never show the rest.
 */
export default function useWalletReveal(): WalletRow {
  const [isRevealed, setIsRevealed] = useState(false);
  const backstop = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const reportSettled = useCallback(() => {
    clearTimeout(backstop.current);
    setIsRevealed(true);
  }, []);

  const reportPending = useCallback(() => {
    clearTimeout(backstop.current);
    backstop.current = setTimeout(() => setIsRevealed(true), WALLET_REVEAL_TIMEOUT);
  }, []);

  useEffect(() => () => clearTimeout(backstop.current), []);

  return useMemo(
    () => ({
      primary: { isRevealed: true, reportSettled, reportPending },
      secondary: { isRevealed, reportSettled: NOTHING_TO_REPORT },
    }),
    [isRevealed, reportSettled, reportPending]
  );
}
